<template>
    <div class="hiw" :dir="isRtl ? 'rtl' : 'ltr'">
        <div class="hiw-page">

            <div class="hiw-langSwitch">
                <button v-for="l in ['he', 'en', 'fr']" :key="l"
                    class="hiw-langBtn" :class="{ active: lang === l }"
                    @click="lang = l">
                    {{ langLabels[l] }}
                </button>
            </div>

            <header class="hiw-masthead">
                <div class="hiw-eyebrow">{{ t.eyebrow }}</div>
                <h1>{{ t.titleStart }} <span>{{ t.titleAccent }}</span>{{ t.titleEnd }}</h1>
                <p class="hiw-mastheadDesc">{{ t.heroDesc }}</p>
            </header>

            <!-- 01 — Concept -->
            <div class="hiw-section">
                <div class="hiw-sectionNum">01</div>
                <div class="hiw-sectionBody">
                    <div class="hiw-sectionLabel">{{ t.s1Label }}</div>
                    <h2>{{ t.s1Title }}</h2>
                    <p v-for="(para, i) in t.s1Paras" :key="i">{{ para }}</p>
                </div>
            </div>

            <!-- 02 — Les 3 écrans -->
            <div class="hiw-section">
                <div class="hiw-sectionNum">02</div>
                <div class="hiw-sectionBody">
                    <div class="hiw-sectionLabel">{{ t.s2Label }}</div>
                    <h2>{{ t.s2Title }}</h2>
                    <p>{{ t.s2Intro }}</p>

                    <div class="hiw-phaseFlow">
                        <div v-for="phase in t.phases" :key="phase.num" class="hiw-phase">
                            <div class="hiw-phaseDot">{{ phase.num }}</div>
                            <div class="hiw-phaseContent">
                                <div class="hiw-phaseTitle">{{ phase.title }}</div>
                                <div class="hiw-phaseDesc">{{ phase.desc }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 03 — Types de jeu -->
            <div class="hiw-section">
                <div class="hiw-sectionNum">03</div>
                <div class="hiw-sectionBody">
                    <div class="hiw-sectionLabel">{{ t.s3Label }}</div>
                    <h2>{{ t.s3Title }}</h2>
                    <p>{{ t.s3Intro }}</p>

                    <div class="hiw-missionsGrid">
                        <div v-for="(m, i) in t.missions" :key="i" class="hiw-missionChip">
                            <div class="hiw-missionIcon">{{ m.icon }}</div>
                            <div class="hiw-missionName">{{ m.name }}</div>
                            <div class="hiw-missionDesc">{{ m.desc }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 04 — Les deux formules -->
            <div class="hiw-section">
                <div class="hiw-sectionNum">04</div>
                <div class="hiw-sectionBody">
                    <div class="hiw-sectionLabel">{{ t.s4Label }}</div>
                    <h2>{{ t.s4Title }}</h2>

                    <div class="hiw-routeCards">
                        <div class="hiw-routeCard">
                            <div class="hiw-routeCardTitle">{{ t.offerReady.title }}</div>
                            <p>{{ t.offerReady.desc }}</p>
                        </div>
                        <div class="hiw-routeCard hiw-highlight">
                            <div class="hiw-routeCardTitle">{{ t.offerCustom.title }}</div>
                            <p>{{ t.offerCustom.desc }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <hr>

            <div class="hiw-ctaSection">
                <div class="hiw-ctaText">{{ t.ctaText }}</div>
                <a :href="whatsappLink" target="_blank" rel="noopener" class="hiw-whatsappBtn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326M7.994 14.521a6.6 6.6 0 0 1-3.37-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.578 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.2-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.336-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                    </svg>
                    {{ t.ctaButton }}
                </a>
            </div>

            <footer class="hiw-footer">
                <span>{{ t.footerLeft }}</span>
                <span>{{ t.footerRight }}</span>
            </footer>

        </div>
    </div>
</template>

<script>
const TRANSLATIONS = {
    he: {
        eyebrow: "מדריך · קונספט ואופן הפעולה",
        titleStart: "איך בונים",
        titleAccent: "משחק ניווט",
        titleEnd: "?",
        heroDesc: "מדריך ליישובים, קיבוצים ומושבים שרוצים ליצור משחק ניווט משלהם — מהבחירה הראשונית של המסלול ועד לשליחת החומרים הסופיים.",

        s1Label: "קונספט",
        s1Title: "מסלול חוץ, מנוהל מהאפליקציה",
        s1Paras: [
            "המשחק הוא מסלול ניווט חוץ: המשתתפים הולכים ברגל בין נקודות עניין לאורך מסלול שנבחר מראש.",
            "השלב הראשון ביצירת כל משחק הוא פשוט: לבחור את נקודות העניין שבהן יעבור המסלול — המקומות הפיזיים שהמשתתפים יגיעו אליהם.",
        ],

        s2Label: "בכל נקודה",
        s2Title: "שלושה מסכים קבועים",
        s2Intro: "כל נקודה במסלול בנויה לפי אותו מבנה, לא משנה מה תוכנה:",
        phases: [
            { num: "1", title: "אימות מיקום", desc: "ודאות שהמשתתף הגיע למקום הנכון. שתי אפשרויות: תמונה של שלט שבו הוסרה מילה שצריך להשלים, או רשימת תמונות שצריך לבחור מתוכה את מה שרואים בדיוק מאותה נקודה." },
            { num: "2", title: "מידע", desc: "מידע על המקום — טקסט, קובץ שמע, סרטון, או כל שילוב ביניהם." },
            { num: "3", title: "משחק", desc: "תרגיל קצר לסיום הנקודה, מתוך מגוון סוגי משחק שהאפליקציה מציעה." },
        ],

        s3Label: "סוגי משחק",
        s3Title: "מגוון תרגילים למסך השלישי",
        s3Intro: "סוג המשחק נבחר בנפרד לכל נקודה במסלול:",
        missions: [
            { icon: "✅", name: "שאלות אמריקאיות", desc: "שאלה עם מספר תשובות אפשריות לבחירה." },
            { icon: "🧩", name: "פאזל", desc: "הרכבת תמונה שחולקה לחלקים." },
            { icon: "⚖️", name: "נכון / לא נכון", desc: "קביעה מהירה לגבי טענה הקשורה למקום." },
            { icon: "🃏", name: "זיכרון", desc: "מציאת הזוגות התואמים בין קלפים הפוכים." },
            { icon: "🔗", name: "התאמה", desc: "חיבור בין תמונות לטקסטים המתאימים להן." },
        ],

        s4Label: "שתי דרכים",
        s4Title: "איך מקבלים משחק",
        offerReady: {
            title: "🎁 משחק מוכן (לדוגמה: חנוכה, יום העצמאות)",
            desc: "צריך לספק רק את נקודות המסלול. לכל נקודה: תמונה אחת (שלט או משהו שרואים רק מאותו מקום) ומיקום GPS — הכל נשלח בקלות בוואטסאפ, בשתיים-שלוש לחיצות.",
        },
        offerCustom: {
            title: "✨ משחק בהתאמה אישית",
            desc: "בנוסף לתמונה ולמיקום GPS של כל נקודה, צריך לספק גם את תוכן מסך המידע (טקסט / שמע / וידאו) וגם לבחור את סוג המשחק למסך השלישי — מכל מה שהאפליקציה יודעת להציע.",
        },

        ctaText: "יש לכם רעיון למשחק? דברו איתנו בוואטסאפ",
        ctaButton: "לדבר בוואטסאפ",
        whatsappMessage: "שלום, אני מעוניין/ת ליצור משחק ניווט",

        footerLeft: "מדריך ליצירת משחק ניווט",
        footerRight: "eifoany.web.app",
    },
    en: {
        eyebrow: "Guide · Concept & How It Works",
        titleStart: "How to build a",
        titleAccent: "navigation game",
        titleEnd: "?",
        heroDesc: "A guide for villages, kibbutzim and moshavim who want to create their own navigation game — from choosing the route to sending in the final material.",

        s1Label: "Concept",
        s1Title: "An outdoor route, driven from the app",
        s1Paras: [
            "The game is an outdoor navigation route: participants walk between points of interest along a pre-defined path.",
            "The first step of building any game is simple: choose the points of interest where the route will pass — the physical places participants will reach.",
        ],

        s2Label: "At every point",
        s2Title: "Three fixed screens",
        s2Intro: "Every point on the route follows the same structure, whatever its content:",
        phases: [
            { num: "1", title: "Position check", desc: "Confirms the participant has reached the right spot. Two options: a photo of a sign with one word removed that needs to be filled in, or a list of photos from which to choose the one matching exactly what you see from that point." },
            { num: "2", title: "Information", desc: "Information about the place — text, audio, video, or any combination of these." },
            { num: "3", title: "Game", desc: "A short exercise to complete the point, from a range of game types the app offers." },
        ],

        s3Label: "Game types",
        s3Title: "A range of exercises for the third screen",
        s3Intro: "The game type is chosen separately for each point on the route:",
        missions: [
            { icon: "✅", name: "Multiple choice", desc: "A question with several possible answers to pick from." },
            { icon: "🧩", name: "Puzzle", desc: "Reassemble a picture split into pieces." },
            { icon: "⚖️", name: "True / False", desc: "A quick call on a statement related to the place." },
            { icon: "🃏", name: "Memory", desc: "Find the matching pairs among face-down cards." },
            { icon: "🔗", name: "Matching", desc: "Pair up images with their matching text." },
        ],

        s4Label: "Two paths",
        s4Title: "How to get a game",
        offerReady: {
            title: "🎁 Ready-made game (e.g. Hanukkah, Independence Day)",
            desc: "You only need to provide the route's points. For each point: one photo (a sign, or something only visible from that exact spot) and its GPS location — everything sent easily via WhatsApp, in two or three clicks.",
        },
        offerCustom: {
            title: "✨ Custom-made game",
            desc: "In addition to the photo and GPS location for each point, you also need to provide the information screen content (text / audio / video) and choose the game type for the third screen — from everything the app can offer.",
        },

        ctaText: "Have an idea for a game? Talk to us on WhatsApp",
        ctaButton: "Chat on WhatsApp",
        whatsappMessage: "Hello, I'd like to create a navigation game",

        footerLeft: "Navigation game — how it works",
        footerRight: "eifoany.web.app",
    },
    fr: {
        eyebrow: "Guide · Concept et fonctionnement",
        titleStart: "Comment créer un",
        titleAccent: "jeu de navigation",
        titleEnd: " ?",
        heroDesc: "Un guide pour les villages, kibboutz et moshavim qui veulent créer leur propre jeu de navigation — du choix du parcours à l'envoi du matériel final.",

        s1Label: "Concept",
        s1Title: "Un parcours en extérieur, piloté depuis l'app",
        s1Paras: [
            "Le jeu est un parcours de navigation en extérieur : les participants se déplacent à pied entre des points d'intérêt le long d'un itinéraire défini à l'avance.",
            "La première étape de la création d'un jeu est simple : choisir les points d'intérêt où va passer le parcours — les endroits physiques où les participants se rendront.",
        ],

        s2Label: "À chaque point",
        s2Title: "Trois écrans fixes",
        s2Intro: "Chaque point du parcours suit toujours la même structure, quel que soit son contenu :",
        phases: [
            { num: "1", title: "Vérification de position", desc: "Confirme que le participant est bien arrivé au bon endroit. Deux options : la photo d'une pancarte où un mot a été retiré et qu'il faut compléter, ou une liste de photos parmi lesquelles choisir celle qui correspond exactement à ce que l'on voit depuis ce point." },
            { num: "2", title: "Information", desc: "Des informations sur le lieu — texte, audio, vidéo, ou toute combinaison de ces formats." },
            { num: "3", title: "Jeu", desc: "Un court exercice pour valider le point, parmi les différents types de jeu que propose l'application." },
        ],

        s3Label: "Types de jeu",
        s3Title: "Plusieurs exercices possibles pour l'écran 3",
        s3Intro: "Le type de jeu est choisi séparément pour chaque point du parcours :",
        missions: [
            { icon: "✅", name: "QCM", desc: "Une question avec plusieurs réponses possibles parmi lesquelles choisir." },
            { icon: "🧩", name: "Puzzle", desc: "Reconstituer une image découpée en plusieurs pièces." },
            { icon: "⚖️", name: "Vrai / Faux", desc: "Un choix rapide sur une affirmation liée au lieu." },
            { icon: "🃏", name: "Memory", desc: "Retrouver les paires correspondantes parmi des cartes retournées." },
            { icon: "🔗", name: "Association", desc: "Relier des images aux textes qui leur correspondent." },
        ],

        s4Label: "Deux formules",
        s4Title: "Comment obtenir un jeu",
        offerReady: {
            title: "🎁 Jeu clé en main (ex. Hanouka, Yom Haatsmaout)",
            desc: "Il suffit de fournir les points du parcours. Pour chaque point : une photo (pancarte ou élément visible uniquement depuis cet endroit) et sa position GPS — tout s'envoie facilement par WhatsApp, en deux ou trois clics.",
        },
        offerCustom: {
            title: "✨ Jeu sur mesure",
            desc: "En plus de la photo et de la position GPS de chaque point, il faut aussi fournir le contenu de l'écran d'information (texte / audio / vidéo) et choisir le type de jeu pour le troisième écran — parmi tout ce que l'application sait proposer.",
        },

        ctaText: "Vous avez une idée de jeu ? Contactez-nous sur WhatsApp",
        ctaButton: "Discuter sur WhatsApp",
        whatsappMessage: "Bonjour, je souhaite créer un jeu de navigation",

        footerLeft: "Jeu de navigation — fonctionnement",
        footerRight: "eifoany.web.app",
    },
};

export default {
    data() {
        return {
            lang: "he",
            langLabels: { he: "עברית", en: "English", fr: "Français" },
            whatsappNumber: "972526630434",
        };
    },
    computed: {
        t() {
            return TRANSLATIONS[this.lang];
        },
        isRtl() {
            return this.lang === "he";
        },
        whatsappLink() {
            return `https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(this.t.whatsappMessage)}`;
        },
    },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Figtree:ital,wght@0,300;0,400;0,500;0,600;1,400&family=DM+Mono:wght@400;500&display=swap');

.hiw {
    --bg:        #f5f2eb;
    --surface:   #ece9e0;
    --border:    #d4cfc3;
    --text:      #1a1a18;
    --text-2:    #5a5850;
    --text-3:    #8a8678;
    --accent:    #b8770d;
    --accent-lt: #f5e8cc;
    --green:     #3e6b4a;
    --green-lt:  #dceee2;
    --num-col:   #e2ddd4;
    --tag-bg:    #e8e4dc;

    background: var(--bg);
    color: var(--text);
    font-family: 'Figtree', system-ui, sans-serif;
    font-weight: 400;
    line-height: 1.65;

    width: 100vw;
    margin-inline: calc(50% - 50vw);
    min-height: 100vh;
    min-height: 100dvh;
    overflow-x: hidden;
}

.hiw * {
    box-sizing: border-box;
}

.hiw-page {
    max-width: 900px;
    margin: 0 auto;
    padding: 36px 20px 70px;
}

.hiw-langSwitch {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 36px;
}

.hiw-langBtn {
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text-2);
    border-radius: 20px;
    padding: 5px 16px;
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    letter-spacing: 0.04em;
    cursor: pointer;
}

.hiw-langBtn.active {
    background: var(--accent);
    border-color: var(--accent);
    color: white;
}

.hiw-masthead {
    padding-bottom: 32px;
    border-bottom: 1.5px solid var(--border);
    margin-bottom: 48px;
}

.hiw-eyebrow {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-3);
    margin-bottom: 14px;
}

.hiw-masthead h1 {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(32px, 7vw, 56px);
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--text);
    text-wrap: balance;
    margin: 0 0 18px;
}

.hiw-masthead h1 span {
    color: var(--accent);
}

.hiw-mastheadDesc {
    font-size: 16px;
    font-weight: 300;
    color: var(--text-2);
    max-width: 560px;
    line-height: 1.6;
    margin: 0;
}

.hiw-section {
    display: grid;
    grid-template-columns: 64px 1fr;
    gap: 0 28px;
    margin-bottom: 48px;
}

@media (max-width: 560px) {
    .hiw-section {
        grid-template-columns: 1fr;
        gap: 8px 0;
    }
}

.hiw-sectionNum {
    font-family: 'Syne', sans-serif;
    font-size: 52px;
    font-weight: 800;
    color: var(--num-col);
    line-height: 1;
    letter-spacing: -0.03em;
    padding-top: 4px;
    user-select: none;
}

.hiw-sectionLabel {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 8px;
}

.hiw-section h2 {
    font-family: 'Syne', sans-serif;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--text);
    line-height: 1.25;
    text-wrap: balance;
    margin: 0 0 14px;
}

.hiw-section p {
    font-size: 15px;
    color: var(--text-2);
    margin: 0 0 10px;
    max-width: 600px;
}

.hiw-section p:last-child {
    margin-bottom: 0;
}

.hiw-phaseFlow {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    position: relative;
}

.hiw-phaseFlow::before {
    content: '';
    position: absolute;
    inset-inline-start: 17px;
    top: 10px;
    bottom: 10px;
    width: 2px;
    background: var(--border);
}

.hiw-phase {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding-bottom: 22px;
    position: relative;
}

.hiw-phase:last-child {
    padding-bottom: 0;
}

.hiw-phaseDot {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--accent-lt);
    border: 2px solid var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: var(--accent);
    z-index: 1;
}

.hiw-phaseContent {
    padding-top: 6px;
}

.hiw-phaseTitle {
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 4px;
}

.hiw-phaseDesc {
    font-size: 13.5px;
    color: var(--text-2);
    line-height: 1.55;
    max-width: 480px;
}

.hiw-missionsGrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 10px;
    margin-top: 18px;
}

.hiw-missionChip {
    background: var(--tag-bg);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 12px 14px;
}

.hiw-missionIcon {
    font-size: 20px;
    margin-bottom: 6px;
    line-height: 1;
}

.hiw-missionName {
    font-family: 'Syne', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 3px;
}

.hiw-missionDesc {
    font-size: 12px;
    color: var(--text-3);
    line-height: 1.45;
}

.hiw-routeCards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    margin-top: 18px;
}

.hiw-routeCard {
    padding: 16px 18px;
    border: 1.5px solid var(--border);
    border-radius: 12px;
}

.hiw-routeCard.hiw-highlight {
    border-color: var(--green);
    background: var(--green-lt);
}

.hiw-routeCardTitle {
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 6px;
}

.hiw-routeCard.hiw-highlight .hiw-routeCardTitle {
    color: var(--green);
}

.hiw-routeCard p {
    font-size: 13px;
    margin: 0;
}

hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 0 0 44px;
}

.hiw-ctaSection {
    text-align: center;
    margin-bottom: 44px;
}

.hiw-ctaText {
    font-family: 'Syne', sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 16px;
}

.hiw-whatsappBtn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: #25D366;
    color: white;
    text-decoration: none;
    font-weight: 700;
    font-size: 15px;
    padding: 12px 26px;
    border-radius: 30px;
}

.hiw-footer {
    border-top: 1px solid var(--border);
    padding-top: 22px;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: var(--text-3);
    letter-spacing: 0.06em;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}
</style>
